import type { DocumentByPathResult } from './view-document';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  IconButton,
  Input,
  Link,
  Menu,
  Popover,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { FiEdit3, FiMessageCircle, FiMoreVertical } from 'react-icons/fi';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { DocumentMessagesDrawer } from './document-messages-drawer';

import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';
import { toaster } from '#src/components/ui/toaster';
import { UPSERT_DOCUMENT } from '#src/graphql/mutations/upsert-document';
import { useAppConfig } from '#src/providers/app-config-provider';
import { useAppSettings } from '#src/providers/app-settings-provider';
import { usePageHeader } from '#src/providers/page-layout-provider';
import {
  DOCUMENT_PATH_DESCRIPTION,
  isValidDocumentPath,
} from '#src/utils/document-path';
import { formatDocumentName } from '#src/utils/format-document-name';

interface DocumentChildrenButtonProps {
  parentPath: string;
  items: Array<{ id: string; path: string }>;
  hasMore: boolean;
}

function DocumentChildrenButton({
  parentPath,
  items,
  hasMore,
}: DocumentChildrenButtonProps) {
  const countLabel = hasMore ? `${items.length}+` : `${items.length}`;

  return (
    <Popover.Root positioning={{ placement: 'bottom-start' }} size="sm">
      <Popover.Trigger asChild>
        <Button
          variant="ghost"
          size="sm"
          color="fg.muted"
          fontWeight="normal"
          fontSize="sm"
          px={2}
          minW={0}
          title="Child documents"
        >
          🔗 {countLabel}
        </Button>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="320px"
            maxH="300px"
            overflowY="auto"
            p={3}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={1} align="stretch">
              <Text fontSize="xs" fontWeight="semibold" color="fg.muted" mb={1}>
                Child documents
              </Text>
              {items.map((child: { id: string; path: string }) => (
                <Link
                  key={child.id}
                  asChild
                  display="block"
                  fontSize="sm"
                  color="blue.600"
                  _hover={{ textDecoration: 'underline' }}
                >
                  <RouterLink to={`/documents/${child.path}`}>
                    {formatDocumentName(
                      getRelativePath(parentPath, child.path),
                    ).join(' › ')}
                  </RouterLink>
                </Link>
              ))}
              {hasMore && (
                <Text fontSize="xs" color="fg.subtle" fontStyle="italic">
                  More than {items.length} children (showing first{' '}
                  {items.length})
                </Text>
              )}
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

function getRelativePath(parentPath: string, childPath: string): string {
  if (!parentPath) return childPath;
  if (childPath === parentPath) return '';
  const prefix = parentPath + '/';
  return childPath.startsWith(prefix)
    ? childPath.slice(prefix.length)
    : childPath;
}

function DocumentBreadcrumb({ path }: { path: string }) {
  const segments = useMemo(() => {
    const rawSegments = path.split('/').filter((part) => part.length > 0);
    const formatted = formatDocumentName(path);
    const result: Array<{ label: string; accumulated: string }> = [];
    let accumulated = '';

    rawSegments.forEach((segment, index) => {
      accumulated = accumulated ? `${accumulated}/${segment}` : segment;
      result.push({
        label: formatted[index] ?? segment,
        accumulated,
      });
    });

    return result;
  }, [path]);

  if (segments.length === 0) {
    const fallback = formatDocumentName(path).join(' > ') || path;
    return (
      <Text fontSize="lg" fontWeight="semibold" color="fg">
        {fallback}
      </Text>
    );
  }

  return (
    <HStack gap={1} align="center" flexWrap="wrap">
      {segments.map((segment, index) => {
        const isLast = index === segments.length - 1;
        return (
          <Fragment key={segment.accumulated}>
            {isLast ? (
              <Text fontSize="lg" fontWeight="semibold" color="fg">
                {segment.label}
              </Text>
            ) : (
              <Link
                asChild
                fontSize="lg"
                fontWeight="semibold"
                color="blue.600"
                _hover={{ textDecoration: 'underline' }}
              >
                <RouterLink to={`/documents/${segment.accumulated}`}>
                  {segment.label}
                </RouterLink>
              </Link>
            )}
            {!isLast && (
              <Text fontSize="lg" fontWeight="semibold" color="fg.subtle">
                ›
              </Text>
            )}
          </Fragment>
        );
      })}
    </HStack>
  );
}

interface RenameDocumentButtonProps {
  currentPath: string;
  getCurrentValue: () => string;
  onRename: (newPath: string, currentValue: string) => Promise<void>;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function RenameDocumentButton({
  currentPath,
  getCurrentValue,
  onRename,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: RenameDocumentButtonProps) {
  const { documentPathRegex } = useAppSettings();
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const setIsOpen = useCallback(
    (open: boolean) => {
      if (isControlled && controlledOnOpenChange) {
        controlledOnOpenChange(open);
      } else {
        setInternalOpen(open);
      }
    },
    [isControlled, controlledOnOpenChange],
  );
  const [renamePath, setRenamePath] = useState(currentPath);

  useEffect(() => {
    if (isOpen) setRenamePath(currentPath);
  }, [isOpen, currentPath]);

  const trimmedRenamePath = renamePath.trim();
  const isRenamePathValid = trimmedRenamePath
    ? isValidDocumentPath(trimmedRenamePath, documentPathRegex)
    : false;

  const handleRename = async () => {
    if (!trimmedRenamePath || !isRenamePathValid) return;
    try {
      await onRename(trimmedRenamePath, getCurrentValue());
      setIsOpen(false);
    } catch {
      // Parent shows toaster; keep popover open
    }
  };

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(e) => setIsOpen(e.open)}
      positioning={{ placement: 'bottom-end' }}
      size="sm"
    >
      <Popover.Trigger asChild>
        <IconButton
          aria-label="Rename document"
          variant="ghost"
          size="sm"
          title="Rename document"
        >
          <FiEdit3 />
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            maxW="400px"
            p={4}
            bg="bg"
            border="1px solid"
            borderColor="border"
            shadow="lg"
            borderRadius="md"
          >
            <Popover.Arrow>
              <Popover.ArrowTip />
            </Popover.Arrow>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" fontWeight="semibold" color="fg">
                Rename document
              </Text>
              <Text fontSize="xs" color="fg.muted">
                Enter the new path for this document.
              </Text>
              <input
                placeholder="e.g. folder/doc-name or path (with parens)"
                value={renamePath}
                onChange={(e) => setRenamePath(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void handleRename();
                  if (e.key === 'Escape') setIsOpen(false);
                }}
              />
              {trimmedRenamePath && !isRenamePathValid && (
                <Text fontSize="xs" color="red.500">
                  {DOCUMENT_PATH_DESCRIPTION}
                </Text>
              )}
              <HStack gap={2} justify="flex-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={() => void handleRename()}
                  disabled={!trimmedRenamePath || !isRenamePathValid}
                >
                  Rename
                </Button>
              </HStack>
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

interface ViewDocumentHeaderProps {
  document: DocumentByPathResult;
  path: string;
  displayPath: string;
  documentId?: string;
  isNewDocument: boolean;
  childrenItems: Array<{ id: string; path: string }>;
  childrenHasMore: boolean;
  onNavigateToHistory: () => void;
  hasChanges: boolean;
  getCurrentValue: () => string;
  refetch: () => Promise<unknown>;
  onAfterSave: (savedValue: string) => void;
  isCreateSubpageOpen: boolean;
  onCreateSubpageOpenChange: (open: boolean) => void;
}

export function ViewDocumentHeader({
  document,
  path,
  displayPath,
  documentId,
  isNewDocument,
  childrenItems,
  childrenHasMore,
  onNavigateToHistory,
  hasChanges,
  getCurrentValue,
  refetch,
  onAfterSave,
  isCreateSubpageOpen,
  onCreateSubpageOpenChange,
}: ViewDocumentHeaderProps) {
  const { setTitle, actionsRef } = usePageHeader();
  const { documentPathRegex } = useAppSettings();
  const navigate = useNavigate();
  const { removeRecentlyOpenedDocumentById } = useAppConfig();
  const [upsertDocument, { loading: saving }] = useMutation(UPSERT_DOCUMENT);
  const [isMessagesDrawerOpen, setIsMessagesDrawerOpen] = useState(false);
  const [isAdvancedModalOpen, setIsAdvancedModalOpen] = useState(false);
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [createSubpageSegment, setCreateSubpageSegment] = useState('');
  const getCurrentValueRef = useRef(getCurrentValue);
  const [rawValueSnapshot, setRawValueSnapshot] = useState<string>('');

  // Keep a stable ref to the current editor value getter; prevents heavy computations
  // from running on every parent render while the modal is open.
  useEffect(() => {
    getCurrentValueRef.current = getCurrentValue;
  }, [getCurrentValue]);

  // Snapshot the raw value once when the modal opens for a consistent preview/copy.
  useEffect(() => {
    if (!isAdvancedModalOpen) return;
    try {
      setRawValueSnapshot(getCurrentValueRef.current());
    } catch {
      setRawValueSnapshot('');
    }
  }, [isAdvancedModalOpen]);

  useEffect(() => {
    if (isCreateSubpageOpen) setCreateSubpageSegment('');
  }, [isCreateSubpageOpen]);

  const hasChildren = childrenItems.length > 0;
  const documentPathForRename =
    document?.__typename === 'Document' ? document.path : displayPath;

  const formattedName = formatDocumentName(displayPath).join(' > ');

  const trimmedSegment = createSubpageSegment.trim();
  const fullCreateSubpagePath = displayPath
    ? `${displayPath}/${trimmedSegment}`
    : trimmedSegment;
  const isCreateSubpagePathValid =
    !!trimmedSegment &&
    isValidDocumentPath(fullCreateSubpagePath, documentPathRegex);

  const handleCreateSubpage = useCallback(() => {
    const trimmed = trimmedSegment;
    const fullPath = displayPath ? `${displayPath}/${trimmed}` : trimmed;
    if (!trimmed) return;

    onCreateSubpageOpenChange(false);
    setCreateSubpageSegment('');
    navigate(`/documents/${fullPath}`);
  }, [trimmedSegment, displayPath, navigate, onCreateSubpageOpenChange]);

  const handleRename = useCallback(
    async (newPath: string, currentValue: string) => {
      if (document?.__typename !== 'Document') return;
      try {
        await upsertDocument({
          variables: {
            input: {
              id: document.id,
              path: newPath,
              value: currentValue,
            },
          },
        });
        toaster.create({
          title: 'Success',
          description: 'Document renamed.',
        });
        removeRecentlyOpenedDocumentById(document.id);
        await refetch();
        navigate(`/documents/${newPath}`, { replace: true });
      } catch (error) {
        toaster.create({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Unknown error',
        });
      }
    },
    [
      document,
      navigate,
      refetch,
      removeRecentlyOpenedDocumentById,
      upsertDocument,
    ],
  );

  const handleSave = useCallback(async () => {
    const value = getCurrentValue();
    try {
      const { data } = await upsertDocument({
        variables: {
          input: {
            id: documentId ?? path,
            path: isNewDocument ? path : displayPath,
            value,
            format: 'editorjs-v1',
          },
        },
      });

      toaster.create({
        title: 'Success',
        description: isNewDocument
          ? 'Document created successfully'
          : 'Document saved successfully',
      });

      const savedValueString = data?.upsertDocument?.document?.value ?? value;
      onAfterSave(savedValueString);

      await refetch();

      if (isNewDocument) {
        navigate(`/documents/${path}`, { replace: true });
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }, [
    documentId,
    path,
    displayPath,
    isNewDocument,
    getCurrentValue,
    upsertDocument,
    refetch,
    navigate,
    onAfterSave,
  ]);

  const actionsContent =
    !isNewDocument && hasChanges ? (
      <Button
        size="sm"
        colorScheme="blue"
        onClick={handleSave}
        loading={saving}
        loadingText="Saving..."
      >
        Save
      </Button>
    ) : null;

  useEffect(() => {
    const rightControls = (
      <HStack gap={2} align="center">
        {hasChildren && (
          <DocumentChildrenButton
            parentPath={displayPath}
            items={childrenItems}
            hasMore={childrenHasMore}
          />
        )}
        {documentId && (
          <IconButton
            aria-label="Document messages"
            variant="ghost"
            size="sm"
            title="Messages for this document"
            onClick={() => setIsMessagesDrawerOpen(true)}
          >
            <FiMessageCircle />
          </IconButton>
        )}
        <Menu.Root positioning={{ placement: 'bottom-end' }}>
          <Menu.Trigger asChild>
            <IconButton aria-label="Document actions" variant="ghost" size="sm">
              <FiMoreVertical />
            </IconButton>
          </Menu.Trigger>
          <Portal>
            <Menu.Positioner>
              <Menu.Content>
                {!isNewDocument && (
                  <>
                    <Menu.Item
                      value="rename"
                      onClick={() => setIsRenameOpen(true)}
                    >
                      Rename
                    </Menu.Item>
                    <Menu.Item value="history" onClick={onNavigateToHistory}>
                      History
                    </Menu.Item>
                    {documentId && (
                      <Menu.Item
                        value="advanced"
                        onClick={() => setIsAdvancedModalOpen(true)}
                      >
                        Advanced
                      </Menu.Item>
                    )}
                  </>
                )}
              </Menu.Content>
            </Menu.Positioner>
          </Portal>
        </Menu.Root>
        {!isNewDocument && (
          <RenameDocumentButton
            currentPath={documentPathForRename}
            getCurrentValue={getCurrentValue}
            onRename={handleRename}
            open={isRenameOpen}
            onOpenChange={setIsRenameOpen}
          />
        )}
        {isNewDocument && (
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleSave}
            loading={saving}
            loadingText="Saving..."
          >
            Save
          </Button>
        )}
      </HStack>
    );

    const titleContent = (
      <HStack
        gap={4}
        align="center"
        justify="space-between"
        width="100%"
        minW={0}
      >
        <Box flex="1" minW={0}>
          <DocumentBreadcrumb path={displayPath} />
        </Box>
        {rightControls}
      </HStack>
    );
    setTitle(titleContent);

    return () => setTitle(null);
  }, [
    childrenItems,
    childrenHasMore,
    displayPath,
    document,
    documentId,
    documentPathForRename,
    formattedName,
    getCurrentValue,
    hasChildren,
    isNewDocument,
    isRenameOpen,
    onNavigateToHistory,
    handleRename,
    handleSave,
    saving,
    setTitle,
  ]);

  return (
    <>
      {actionsRef.current &&
        actionsContent &&
        createPortal(actionsContent, actionsRef.current)}

      {documentId && (
        <DocumentMessagesDrawer
          documentId={documentId}
          open={isMessagesDrawerOpen}
          onOpenChange={setIsMessagesDrawerOpen}
        />
      )}

      <DialogRoot
        open={isAdvancedModalOpen}
        onOpenChange={(e) => setIsAdvancedModalOpen(e.open)}
      >
        <CenteredDialogContent>
          <DialogHeader>
            <DialogTitle>Advanced</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={5} align="stretch">
              <Box>
                <Text fontSize="sm" color="fg.muted" mb={2}>
                  Document ID
                </Text>
                <HStack gap={2} align="flex-start">
                  <Text
                    fontFamily="mono"
                    fontSize="sm"
                    color="fg"
                    wordBreak="break-all"
                    flex={1}
                  >
                    {documentId ?? '—'}
                  </Text>
                  {documentId && (
                    <IconButton
                      size="sm"
                      variant="ghost"
                      aria-label="Copy document ID"
                      title="Copy to clipboard"
                      onClick={() => {
                        void navigator.clipboard
                          .writeText(documentId)
                          .then(() => {
                            toaster.create({
                              title: 'Copied',
                              description: 'Document ID copied to clipboard',
                              type: 'success',
                            });
                          })
                          .catch(() => {
                            toaster.create({
                              title: 'Copy failed',
                              description: 'Could not copy to clipboard',
                              type: 'error',
                            });
                          });
                      }}
                    >
                      📋
                    </IconButton>
                  )}
                </HStack>
              </Box>

              <Box>
                <HStack
                  gap={2}
                  align="flex-start"
                  justify="space-between"
                  mb={2}
                >
                  <Text fontSize="sm" color="fg.muted">
                    Raw value
                  </Text>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label="Copy raw value"
                    title="Copy raw value to clipboard"
                    disabled={!rawValueSnapshot}
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(rawValueSnapshot)
                        .then(() => {
                          toaster.create({
                            title: 'Copied',
                            description: 'Raw value copied to clipboard',
                            type: 'success',
                          });
                        })
                        .catch(() => {
                          toaster.create({
                            title: 'Copy failed',
                            description: 'Could not copy to clipboard',
                            type: 'error',
                          });
                        });
                    }}
                  >
                    📋
                  </IconButton>
                </HStack>

                <Box
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor="border"
                  overflowY="auto"
                  maxH="280px"
                  p={2}
                >
                  <Text
                    as="pre"
                    fontFamily="mono"
                    fontSize="xs"
                    color="fg"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                    m={0}
                  >
                    {rawValueSnapshot || '—'}
                  </Text>
                </Box>
              </Box>
            </VStack>
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline">Close</Button>
            </DialogActionTrigger>
          </DialogFooter>
        </CenteredDialogContent>
      </DialogRoot>

      <DialogRoot
        open={isCreateSubpageOpen}
        onOpenChange={(e) => onCreateSubpageOpenChange(e.open)}
      >
        <CenteredDialogContent>
          <DialogHeader>
            <DialogTitle>Create subpage</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <VStack gap={3} align="stretch">
              <Text fontSize="sm" color="fg.muted">
                Enter the next part of the path for a child of{' '}
                <Text as="span" fontWeight="semibold" color="fg">
                  {displayPath || '/'}
                </Text>
                .
              </Text>
              <Input
                placeholder={
                  displayPath
                    ? 'e.g. new-page or nested/page'
                    : 'e.g. my-doc or folder/doc'
                }
                value={createSubpageSegment}
                onChange={(e) => setCreateSubpageSegment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void handleCreateSubpage();
                  }
                  if (e.key === 'Escape') {
                    onCreateSubpageOpenChange(false);
                    setCreateSubpageSegment('');
                  }
                }}
                size="sm"
                autoFocus
                aria-invalid={!!trimmedSegment && !isCreateSubpagePathValid}
                borderColor={
                  trimmedSegment && !isCreateSubpagePathValid
                    ? 'red.500'
                    : undefined
                }
              />
              {trimmedSegment && !isCreateSubpagePathValid && (
                <Text fontSize="xs" color="red.500">
                  {DOCUMENT_PATH_DESCRIPTION}
                </Text>
              )}
            </VStack>
          </DialogBody>
          <DialogFooter>
            <HStack gap={2} justify="flex-end" width="100%">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onCreateSubpageOpenChange(false);
                  setCreateSubpageSegment('');
                }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={() => {
                  void handleCreateSubpage();
                }}
                disabled={!isCreateSubpagePathValid}
              >
                Create
              </Button>
            </HStack>
          </DialogFooter>
        </CenteredDialogContent>
      </DialogRoot>
    </>
  );
}
